import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { OneDataCollection, SectionHeader } from '@factorialco/f0-react/dist/experimental'
import { Money, Plane } from '@factorialco/f0-react/icons/app'
import {
  DistributorProductVariantsDocument,
  DistributorProductVariantsQuery,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import RequestDeviceModal from 'modules/it_management/components/RequestDeviceModal'
import { useItManagementFeatureAvailability } from 'modules/it_management/hooks/useItManagementFeatureAvailability'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import emptyStateImage from './Empty-state-device-catalog-card.png'

type DistributorProductVariant =
  DistributorProductVariantsQuery['itManagement']['distributorProductVariantsConnection']['nodes'][number]

const truncateDescription = (text: string | null | undefined, maxLines = 2): string => {
  if (!text) return '-'
  const lines = text.split('\n')
  if (lines.length <= maxLines) return text
  return lines.slice(0, maxLines).join('\n') + '...'
}

const getDescription = (variant: DistributorProductVariant, maxLines = 2): string => {
  const description = variant.description || variant.product?.description || null
  return truncateDescription(description, maxLines)
}

const formatPrice = (
  cost: number | string | null | undefined,
  period: number | null | undefined
): string => {
  if (!cost) return '-'
  const costNumber = typeof cost === 'string' ? parseFloat(cost) : cost
  if (isNaN(costNumber)) return '-'
  if (period && period > 0) {
    return `From ${costNumber.toFixed(2)} € /month`
  }
  return `${costNumber.toFixed(2)} €`
}

const DeviceCatalogPage: React.FC = () => {
  const navigate = useNavigate()
  const {
    isItManagementEnabled,
    loading: featureLoading,
    error: featureError,
  } = useItManagementFeatureAvailability()

  const [selectedVariant, setSelectedVariant] = useState<DistributorProductVariant | null>(null)

  const source = useGraphqlDataCollectionSource({
    document: DistributorProductVariantsDocument,
    fetchPolicy: 'network-only',
    perPage: 12,
    mapData: (data: DistributorProductVariantsQuery) => {
      return data?.itManagement.distributorProductVariantsConnection?.nodes || []
    },
    mapPagination: (data: DistributorProductVariantsQuery) => {
      return {
        total: data?.itManagement.distributorProductVariantsConnection?.totalCount || 0,
      }
    },
    mapVariables: () => {
      const variables: { ids?: string[] } = {}

      return variables
    },
    itemActions: (item: DistributorProductVariant) => [
      {
        label: i18n.t('it_management.device_catalog.actions.request'),
        type: 'primary',
        onClick: (event?: React.MouseEvent) => {
          event?.stopPropagation()
          setSelectedVariant(item)
        },
      },
    ],
    itemOnClick: (item: DistributorProductVariant) => {
      return () => {
        navigate(`/device-catalog/${item.id}`)
      }
    },
  })

  return (
    <ResponsivePage.Body
      authorized={isItManagementEnabled}
      error={featureError}
      loading={featureLoading}
      variant="full"
    >
      <SectionHeader
        title={i18n.t('it_management.device_catalog.description.title')}
        description={i18n.t('it_management.device_catalog.description.subtitle')}
      />
      <OneDataCollection
        source={source}
        emptyStates={{
          'no-data': {
            title: i18n.t('it_management.device_catalog.empty_state.title'),
            description: i18n.t('it_management.device_catalog.empty_state.description'),
          },
        }}
        visualizations={[
          {
            type: 'card',
            options: {
              title: (item: DistributorProductVariant) => {
                return item.product?.brand && item.product?.name
                  ? `${item.product.brand} ${item.product.name}`
                  : item.sku || item.id
              },
              description: (item: DistributorProductVariant) => {
                return getDescription(item, 2)
              },
              image: (item: DistributorProductVariant) => {
                return item.firstProductMediaImageUrl || emptyStateImage
              },
              cardProperties: [
                {
                  label: '',
                  icon: Money,
                  render: (item: DistributorProductVariant) => {
                    const price = formatPrice(item.firstOfferCost, item.firstOfferPeriod)
                    if (price === '-') return undefined
                    return {
                      type: 'text',
                      value: price,
                    }
                  },
                },
                {
                  label: '',
                  icon: Plane,
                  render: (item: DistributorProductVariant) => {
                    const deliveryInfo =
                      item.deliveryConditions && item.deliveryConditions !== '-'
                        ? item.deliveryConditions
                        : i18n.t('it_management.device_catalog.standard_delivery')
                    return {
                      type: 'text',
                      value: deliveryInfo,
                    }
                  },
                },
              ],
            },
          },
        ]}
      />
      {selectedVariant && (
        <RequestDeviceModal variant={selectedVariant} onClose={() => setSelectedVariant(null)} />
      )}
    </ResponsivePage.Body>
  )
}

export default DeviceCatalogPage
